import Phaser from 'phaser';
import { musicManager } from '../audio/MusicManager';
import BotController from '../BotController';
import { showBanner } from '../AdManager';
import { incrementGameCount } from '../RatingManager';

const FOODS = ['burger', 'burrito', 'carrot', 'cheese', 'cookies', 'cupcake', 'fries', 'grapes', 'icecream', 'pizza', 'popcorn', 'soda', 'taco', 'watermelon', 'eggplant'];

// ── Base design resolution ────────────────────────────────────────
const BASE_WIDTH = 420;

// Populated in create() once we know the real screen width
let S = 1;

// ── Food config at base resolution (420px wide) ───────────────────
const BASE_FOOD_CONFIG = {
  burger:     { w: 173,  h: 138,  density: 1.6, friction: 0.9 },
  burrito:    { w: 115,  h: 196,  density: 1.2, friction: 0.9 },
  carrot:     { w: 104,  h: 201,  density: 0.9, friction: 0.9 },
  cheese:     { w: 150,  h: 115,  density: 1.2, friction: 0.9 },
  cookies:    { w: 161,  h: 150,  density: 1.0, friction: 0.9 },
  cupcake:    { w: 121,  h: 173,  density: 1.0, friction: 0.9 },
  fries:      { w: 127,  h: 184,  density: 0.9, friction: 0.9 },
  grapes:     { w: 121,  h: 144,  density: 1.0, friction: 0.9 },
  icecream:   { w: 109,  h: 178,  density: 0.9, friction: 0.9 },
  pizza:      { w: 173,  h: 173,  density: 1.2, friction: 0.9 },
  popcorn:    { w: 138,  h: 161,  density: 0.9, friction: 0.9 },
  soda:       { w: 115,  h: 150,  density: 1.3, friction: 0.9 },
  taco:       { w: 161,  h: 127,  density: 1.1, friction: 0.9 },
  watermelon: { w: 151,  h: 130,  density: 1.6, friction: 0.9 },
  eggplant:   { w: 115,  h: 190,  density: 1.0, friction: 0.9 },
};

const WEB_SCALE = 0.5;
const cfg = (name) => ({
  ...BASE_FOOD_CONFIG[name],
  w: BASE_FOOD_CONFIG[name].w * S * WEB_SCALE,
  h: BASE_FOOD_CONFIG[name].h * S * WEB_SCALE,
});

const PIXEL_SCALE          = 50;
const toR                  = (px) => px / PIXEL_SCALE;
const toPx                 = (r)  => r  * PIXEL_SCALE;
const DEBUG_PHYSICS        = false;
const PLATFORM_WIDTH_RATIO = 0.80;
const ROTATE_STEP          = Phaser.Math.DegToRad(30);

export default class GameScene extends Phaser.Scene {
  constructor() {
    super({ key: 'GameScene' });
    this.RAPIER        = null;
    this.rapierWorld   = null;
    this.foodBodies    = [];
    this.shapes        = null;
    this.scaledShapes  = {};
    this.foodQueue     = [];
    this.currentFoodName    = null;
    this.previewImage  = null;
    this.previewAngle  = 0;
    this.aimX          = 0;
    this.canDrop       = false;
    this.score         = 0;
    this.gameOver      = false;
    this.groundY       = 0;
    this.platformW     = 0;
    this.platformX     = 0;
    this.spawnAboveStack = 0;
    this.aimLine       = null;
    this.aimArrow      = null;
    this.debugGraphics = null;
    this.maxTowerHeight   = 0;
    this.currentSpawnY    = 0;
    this.fallenCheckTimer = null;
    this.S             = 1;
    this.logicalHeight = 0;
    this.ignoreDrop = false;
    this.pauseContainer = null;
    // Rescue state
    this.rescueUsed         = false;
    this.lastStableSnapshot = null;
    // Bot mode state
    this.gameMode      = 'solo';
    this.currentTurn   = 'player';
    this.lastDropper   = 'player';
    this.botController = null;
    this.botAnimTween  = null;
    this.playerScore   = 0;
    this.botScore      = 0;
    // Rotation counter for ads
    this.rotationsLeft = 12;
    this.nextFoodRevealed = false;
  }

  /* ─────────────────────────── INIT ────────────────────────────── */
  init(data) {
    this.gameMode = (data && data.mode === 'vs_bot') ? 'vs_bot' : 'solo';
  }

  /* ─────────────────────────── PRELOAD ─────────────────────────── */
  preload() {
    FOODS.forEach(name => {
      this.load.image(name, `assets/images/${name}.png`);
    });
    this.load.json('shapes', 'assets/shapes.json');
  }

  /* ─────────────────────────── CREATE ──────────────────────────── */
  create() {
    showBanner();

    const W = this.scale.width;
    const H = this.scale.height;
    const dpr = window.devicePixelRatio || 1;

    this.S = Math.min((W / dpr) / BASE_WIDTH, 1.8);
    S = Math.min(W / BASE_WIDTH, 1.8);

    FOODS.forEach(name => {
      const texture = this.textures.get(name);
      texture.setFilter(Phaser.Textures.FilterMode.LINEAR);
    });

    this.RAPIER = window.__RAPIER__;
    if (!this.RAPIER) { console.error('Rapier WASM not ready.'); return; }

    this.groundY         = H - H * 0.24;
    this.platformW       = W * PLATFORM_WIDTH_RATIO;
    this.platformX       = W / 2;
    this.spawnAboveStack = 100 * S;

    this.buildWorld();

    this.shapes = this.cache.json.get('shapes');
    FOODS.forEach(name => {
      this.scaledShapes[name] = this.extractScaledPolygons(name);
    });

    if (DEBUG_PHYSICS) this.debugGraphics = this.add.graphics().setDepth(999);

    // ── Input ─────────────────────────────────────────────────────

    // Only move food when actively dragging (pointer held down)
    this.input.on('pointermove', pointer => {
      if (!this.canDrop || this.gameOver) return;
      if (!pointer.isDown) return;
      // Only move if finger has dragged more than 20px from press point
      const dragDistance = Phaser.Math.Distance.Between(
        pointer.downX, pointer.downY,
        pointer.x, pointer.y
      );
      if (dragDistance < 20) return;
      const half = this.platformW / 2;
      this.aimX = Phaser.Math.Clamp(
        pointer.x,
        this.platformX - half - 20,
        this.platformX + half + 20
      );
    });

    // Tap anywhere above bottom 15% = drop at current position
    this.input.on('pointerup', pointer => {
      if (!this.canDrop || this.gameOver) return;
      if (this.ignoreDrop) return;
      if (this.ignoreRotate) return;
      if (pointer.y > this.scale.height * 0.76) return; // ignore bottom (button + banner area)
      const dragDistance = Phaser.Math.Distance.Between(
        pointer.downX, pointer.downY,
        pointer.upX,   pointer.upY
      );
      if (dragDistance < 20) this.dropCurrentFood(); // only drop on tap, not drag release
    });

    if (this.gameMode === 'vs_bot') {
      this.botController = new BotController(this);
    }

    this.startGame();

    if (!this.scene.isActive('UIScene')) {
      this.scene.launch('UIScene', { mode: this.gameMode });
    }
  }

  /* ─────────────────── BUILD STATIC WORLD ─────────────────────── */
  buildWorld() {
    const W = this.scale.width;
    const H = this.scale.height;

    // ═══════════════════════════════════════════════════════════
    //  COTTON CANDY THEME
    //  Palette: blush pink · sky blue · soft lavender · white
    // ═══════════════════════════════════════════════════════════

    // ── Layer -5 : airy pastel gradient base ──────────────────────────
    // Top-left blush → top-right sky blue → bottom warm lavender
    const base = this.add.graphics().setDepth(-5).setScrollFactor(0);
    base.fillGradientStyle(0xf8f0ff, 0xebe8ff, 0xf0e8ff, 0xf8f0ff, 1);
    base.fillRect(0, 0, W, H);

    // ── Layer -4 : soft pastel bloom orbs (depth & warmth) ────────────
    // Large, very low-opacity blobs give the impression of light bouncing
    // through spun sugar — keeps the look airy, not flat
    const bloom = this.add.graphics().setDepth(-4).setScrollFactor(0);
    [
      { x: W * 0.10, y: H * 0.15, r: W * 0.55, col: 0xff9ec5, a: 0.12 }, // pink
      { x: W * 0.90, y: H * 0.10, r: W * 0.50, col: 0x99ccff, a: 0.11 }, // blue
      { x: W * 0.50, y: H * 0.60, r: W * 0.55, col: 0xcc99ff, a: 0.09 }, // lavender
      { x: W * 0.15, y: H * 0.80, r: W * 0.40, col: 0xcc99ff, a: 0.10 }, // pink
      { x: W * 0.85, y: H * 0.75, r: W * 0.38, col: 0xaad4ff, a: 0.10 }, // blue
    ].forEach(({ x, y, r, col, a }) => {
      for (let i = 5; i >= 1; i--) {
        bloom.fillStyle(col, (a / 5) * i);
        bloom.fillCircle(x, y, r * (i / 5));
      }
    });

    // ── Layer -3 : cotton candy puffs (fluffy background clouds) ──────
    // Pink and blue clusters of overlapping soft circles
    const puffData = [
      { x: W * 0.08, y: H * 0.22, scale: 1.2 * S, col: 0xcc99ff, speed: 0.10 },
      { x: W * 0.75, y: H * 0.14, scale: 1.0 * S, col: 0xaad4ff, speed: 0.08 },
      { x: W * 0.45, y: H * 0.40, scale: 0.9 * S, col: 0xcc99ff, speed: 0.12 },
      { x: W * 0.88, y: H * 0.50, scale: 0.8 * S, col: 0xcc99ff, speed: 0.07 },
      { x: W * 0.20, y: H * 0.65, scale: 1.1 * S, col: 0xaad4ff, speed: 0.09 },
      { x: W * 0.60, y: H * 0.78, scale: 0.7 * S, col: 0xcc99ff, speed: 0.11 },
    ];
    this.puffs = puffData.map(pd => {
      const g = this.add.graphics().setDepth(-3).setScrollFactor(0);
      this.drawCottonPuff(g, pd.col, pd.scale);
      g.x = pd.x;
      g.y = pd.y;
      g.speedX = pd.speed * S;
      return g;
    });

    // ── Layer -2 : soft sparkle dots scattered across screen ──────────
    // Tiny circles (not harsh crosses) — stay true to the soft theme
    const dotGfx = this.add.graphics().setDepth(-2).setScrollFactor(0);
    const dotColors = [0xffffff, 0xffcce6, 0xccddff, 0xeeccff];
    for (let i = 0; i < 55; i++) {
      const dx  = Phaser.Math.Between(0, W);
      const dy  = Phaser.Math.Between(0, H);
      const dr  = Phaser.Math.FloatBetween(1.0, 3.5);
      const da  = Phaser.Math.FloatBetween(0.20, 0.75);
      dotGfx.fillStyle(Phaser.Math.RND.pick(dotColors), da);
      dotGfx.fillCircle(dx, dy, dr);
    }

    // ── Layer -2 : twinkling ✦ sparkles ───────────────────────────────
    for (let i = 0; i < 14; i++) {
      const sx  = Phaser.Math.Between(8, W - 8);
      const sy  = Phaser.Math.Between(8, H - 8);
      const sz  = Phaser.Math.FloatBetween(2.5, 8) * S;
      const col = Phaser.Math.RND.pick([0xffffff, 0xffcce6, 0xccddff, 0xeeccff]);
      const sp  = this.add.graphics().setDepth(-2).setScrollFactor(0);
      sp.fillStyle(col, 1);
      sp.fillRect(-sz / 6, -sz / 2, sz / 3, sz);
      sp.fillRect(-sz / 2, -sz / 6, sz, sz / 3);
      sp.x = sx;
      sp.y = sy;
      this.tweens.add({
        targets:  sp,
        alpha:    { from: 0.05, to: 0.90 },
        scaleX:   { from: 0.20, to: 1.00 },
        scaleY:   { from: 0.20, to: 1.00 },
        duration: Phaser.Math.Between(900, 2800),
        yoyo:     true, repeat: -1,
        delay:    Phaser.Math.Between(0, 3000),
        ease:     'Sine.easeInOut',
      });
    }

    // ── Layer -1 : floating pastel bubbles ────────────────────────────
    const bubbleColors = [0xcc99ff, 0xaad4ff, 0xffffff, 0xf0c8ff, 0xf0d6ff, 0xc8e0ff];
    this.bubbles = [
      { x: W * 0.10, y: H * 0.65, r: 14, ci: 0, speed: 0.28 },
      { x: W * 0.28, y: H * 0.30, r: 9,  ci: 1, speed: 0.18 },
      { x: W * 0.50, y: H * 0.80, r: 18, ci: 2, speed: 0.32 },
      { x: W * 0.72, y: H * 0.45, r: 11, ci: 3, speed: 0.22 },
      { x: W * 0.88, y: H * 0.20, r: 15, ci: 4, speed: 0.25 },
      { x: W * 0.38, y: H * 0.55, r: 8,  ci: 5, speed: 0.16 },
      { x: W * 0.62, y: H * 0.15, r: 16, ci: 1, speed: 0.20 },
      { x: W * 0.18, y: H * 0.92, r: 10, ci: 0, speed: 0.30 },
      { x: W * 0.82, y: H * 0.60, r: 12, ci: 2, speed: 0.24 },
    ].map(bc => {
      const r   = bc.r * S;
      const col = bubbleColors[bc.ci];
      const b   = this.add.graphics().setDepth(-1).setScrollFactor(0);
      b.fillStyle(col, 0.10); b.fillCircle(0, 0, r * 2.0); // soft outer halo
      b.fillStyle(col, 0.28); b.fillCircle(0, 0, r);       // body
      b.lineStyle(Math.max(1, S * 0.7), 0xffffff, 0.70);
      b.strokeCircle(0, 0, r);                              // white rim
      b.fillStyle(0xffffff, 0.65); b.fillCircle(-r*0.28, -r*0.32, r*0.28); // shine
      b.fillStyle(0xffffff, 0.95); b.fillCircle(-r*0.20, -r*0.22, r*0.09); // sparkle dot
      b.x = bc.x;
      b.y = bc.y;
      b.speedY = -bc.speed * S;
      return b;
    });

    // ── Platform : soft candy-floss counter ──────────────────────────
    const px = this.platformX, py = this.groundY;
    const pw = this.platformW,  ph = 16 * S;
    const plat = this.add.graphics().setDepth(2);
    plat.fillStyle(0xcc88ff, 0.25);  // purple shadow halo
    plat.fillRoundedRect(px - pw/2 - 4*S, py - ph/2 - 3*S, pw + 8*S, ph + 9*S, 11*S);
    plat.fillStyle(0xf5eeff, 1);     // creamy white-purple body
    plat.fillRoundedRect(px - pw/2, py - ph/2, pw, ph, 8*S);
    plat.lineStyle(Math.max(1.5, 2*S), 0xbb88ee, 1.0); // soft purple border
    plat.strokeRoundedRect(px - pw/2, py - ph/2, pw, ph, 8*S);
    plat.fillStyle(0xffffff, 0.55);  // glossy top highlight
    plat.fillRoundedRect(px - pw/2 + 5*S, py - ph/2 + 3*S, pw - 10*S, ph * 0.35, 5*S);

    this.aimLine = this.add.graphics().setDepth(4);
  }


  drawCottonPuff(gfx, col, scale) {
    // A cotton candy puff is a cluster of soft overlapping circles
    gfx.fillStyle(col, 0.28);
    [
      { ox:   0, oy:  0,  r: 32 },
      { ox:  30, oy: -8,  r: 26 },
      { ox: -30, oy: -6,  r: 24 },
      { ox:  14, oy:-22,  r: 20 },
      { ox: -14, oy:-20,  r: 19 },
      { ox:  46, oy:  5,  r: 18 },
      { ox: -46, oy:  5,  r: 17 },
      { ox:  28, oy:-30,  r: 15 },
      { ox: -20, oy:-32,  r: 14 },
    ].forEach(b => gfx.fillCircle(b.ox * scale, b.oy * scale, b.r * scale));
    // Lighter inner highlight for fluffiness
    gfx.fillStyle(0xffffff, 0.18);
    gfx.fillCircle(-6 * scale, -12 * scale, 20 * scale);
  }

  /* ────────────── START / RESTART GAME STATE ───────────────────── */
  startGame() {
    this.ignoreRotate = true;
    this.time.delayedCall(500, () => { this.ignoreRotate = false; });
    this.ignoreDrop = true;
    this.time.delayedCall(500, () => { this.ignoreDrop = false; });

    const W = this.scale.width;

    this.foodBodies.forEach(({ rigidBody, image }) => {
      try { this.rapierWorld.removeRigidBody(rigidBody); } catch(e) {}
      if (image && image.scene) image.destroy();
    });
    this.foodBodies = [];

    if (this.rapierWorld) { try { this.rapierWorld.free(); } catch(e) {} }
    this.rapierWorld = new this.RAPIER.World({ x: 0, y: 10 });

    const GROUND_H = Math.max(20, 28 * S);
    const groundBody = this.rapierWorld.createRigidBody(
      this.RAPIER.RigidBodyDesc.fixed()
        .setTranslation(toR(this.platformX), toR(this.groundY + GROUND_H / 2))
    );
    this.rapierWorld.createCollider(
      this.RAPIER.ColliderDesc
        .cuboid(toR(this.platformW / 2), toR(GROUND_H / 2))
        .setFriction(0.9).setRestitution(0.0),
      groundBody
    );

    this.score          = 0;
    this.gameOver       = false;
    this.maxTowerHeight = 0;
    this.logicalHeight  = 0;
    this.aimX           = W / 2;
    this.previewAngle   = 0;
    this.canDrop        = false;
    this.currentTurn        = 'player';
    this.lastDropper        = 'player';
    this.playerScore        = 0;
    this.botScore           = 0;
    this.rescueUsed         = false;
    this.lastStableSnapshot = null;
    this.cameras.main.scrollY = 0;

    if (this.botController) this.botController.reset();

    if (this.previewImage) { this.previewImage.destroy(); this.previewImage = null; }
    this.aimLine.clear();

    if (this.fallenCheckTimer) this.fallenCheckTimer.remove();
    this.fallenCheckTimer = this.time.addEvent({
      delay: 400, callback: () => {
        this.checkFallenFoods();
        // Play a gentle settling thud when foods are visibly shifting
        if (!this.gameOver && this.foodBodies.length >= 2) {
          const anySliding = this.foodBodies.some(({ rigidBody }) => {
            const v  = rigidBody.linvel();
            const av = rigidBody.angvel();
            return Math.abs(v.x) > 1.8 || Math.abs(v.y) > 2.5 || Math.abs(av) > 1.2;
          });
          if (anySliding) musicManager.soundSlide();
        }
      }, callbackScope: this, loop: true,
    });

    this.time.delayedCall(100, () => {
      this.events.emit('scoreUpdate', 0);
      this.events.emit('heightUpdate', 0);
    });

    this.refillQueue();
    this.currentSpawnY = this.getSpawnY();
    this.spawnNextFood();
  }

  restartGame() {
    this.startGame();
    this.events.emit('gameRestarted');
  }

  /* ─────────────────────────── UPDATE ──────────────────────────── */
  update() {
    if (this.bubbles) {
      const H = this.scale.height;
      this.bubbles.forEach(b => {
        b.y += b.speedY;
        if (b.y < -60) b.y = H + 60;
      });
    }
    if (this.puffs) {
      const W = this.scale.width;
      this.puffs.forEach(p => {
        p.x += p.speedX;
        if (p.x > W + 150) p.x = -150;
      });
    }
    if (!this.rapierWorld) return;

    this.rapierWorld.step();

    this.foodBodies.forEach(({ rigidBody, image }) => {
      if (!image || !image.scene) return;
      const pos = rigidBody.translation();
      const rot = rigidBody.rotation();
      image.setPosition(toPx(pos.x), toPx(pos.y));
      image.setRotation(rot);
    });

    if (!this.gameOver) {
      this.currentSpawnY = this.getSpawnY();

      if (this.previewImage) {
        this.previewImage.x = this.aimX;
        this.previewImage.y = this.currentSpawnY;
      }

      if (this.canDrop) {
        this.aimLine.clear();
        this.aimLine.lineStyle(2 * S, 0xffcc00, 0.4);
        this.aimLine.beginPath();
        this.aimLine.moveTo(this.aimX, this.currentSpawnY + 20 * S);
        this.aimLine.lineTo(this.aimX, this.groundY - 10);
        this.aimLine.strokePath();
      }

      // ── Camera scroll ─────────────────────────────────────────────
      const spawnScreenY = this.currentSpawnY - this.cameras.main.scrollY;
      const scrollThreshold = this.scale.height * 0.25; // start scrolling when spawn is in top 25%

      if (spawnScreenY < scrollThreshold) {
        const targetScrollY = this.currentSpawnY - scrollThreshold;
        this.cameras.main.scrollY = Phaser.Math.Linear(
          this.cameras.main.scrollY,
          targetScrollY,
          0.05  // smoothing — increase for faster, decrease for slower
        );
      }
    }

    if (DEBUG_PHYSICS) this.drawDebug();
  }

  /* ──────────────── ROTATE PREVIEW 30° ────────────────────────── */
  async rotatePreview() {
    if (!this.previewImage) return;

    // Import at method level to avoid circular dependency
    const { getAdsRemoved } = await import('../IAPManager');
    const adsRemoved = await getAdsRemoved();

    // If ads removed, unlimited rotations
    if (!adsRemoved) {
      // Check if rotations left
      if (this.rotationsLeft <= 0) {
        this.events.emit('rotationsExhausted');
        return;
      }
      this.rotationsLeft--;
      // Notify UI of rotation count update
      this.events.emit('rotationCountUpdate', this.rotationsLeft);
    }

    this.previewAngle += ROTATE_STEP;
    if (this.previewAngle >= Math.PI * 2) this.previewAngle = 0;
    this.previewImage.setRotation(this.previewAngle);
  }

  refillRotations() {
    this.rotationsLeft = 12;
    this.events.emit('rotationCountUpdate', this.rotationsLeft);
  }

  /* ──────────────────── DYNAMIC SPAWN Y ───────────────────────── */
  getSpawnY() {
    const GAP = 60 * S;
    const c = this.currentFoodName ? cfg(this.currentFoodName) : null;
    const halfH = c ? c.h / 2 : 40 * S;

    if (this.foodBodies.length === 0) {
      return this.groundY - GAP - halfH;
    }

    const topY = Math.min(...this.foodBodies.map(({ image }) => {
      const ac = cfg(image.texture.key);
      return image.y - (ac ? ac.h / 2 : 40 * S);
    }));

    return topY - GAP - halfH;
  }

  /* ──────────────────── TOWER HEIGHT SCORE ─────────────────────── */
  updateTowerHeight() {
    // no longer used every frame
  }

  calculateFinalHeight() {
    if (this.gameOver) return;
    if (this.foodBodies.length === 0) return;

    const topY = Math.min(...this.foodBodies.map(({ image }) => {
      const c = cfg(image.texture.key);
      return image.y - (c ? c.h / 2 : 0);
    }));

    const heightPx = this.groundY - topY;
    if (heightPx <= 0) return;

    if (heightPx > this.maxTowerHeight) {
      this.maxTowerHeight = heightPx;
      this.logicalHeight = Math.floor(heightPx / this.S);
      this.events.emit('heightUpdate', this.logicalHeight);
    }
  }

  /* ─────────────────── CHECK FOR FALLEN FOODS ──────────────────── */
  checkFallenFoods() {
    if (this.gameOver) return;
    const H = this.scale.height;
    const W = this.scale.width;

    for (const { image } of this.foodBodies) {
      if (!image || !image.scene) continue;
      if (image.y > H + 50)                    { this.triggerGameOver(); return; }
      if (image.x < -150 || image.x > W + 150) { this.triggerGameOver(); return; }
    }

    this.foodBodies = this.foodBodies.filter(({ rigidBody, image }) => {
      if (!image || !image.scene) return false;
      if (image.y > H + 300 || image.x < -300 || image.x > W + 300) {
        try { this.rapierWorld.removeRigidBody(rigidBody); } catch(e) {}
        image.destroy();
        return false;
      }
      return true;
    });
  }

  /* ──────────────────── EXTRACT & SCALE POLYGONS ───────────────── */
  extractScaledPolygons(name) {
    if (!this.shapes || !this.shapes[name]) return [];
    const data = this.shapes[name];
    const c    = cfg(name);

    // Collect raw polygons and whether their coords are pre-centered
    // New format: { width, height, origin, shapes: { key: { vertices: [[x,y],...] } } }
    // Old format: { fixtures: [{ vertices: [{x,y},...] }] }
    const rawPolygons = []; // each: { verts, centered, imgW, imgH }

    if (data.shapes && typeof data.shapes === 'object') {
      // New export format — vertices are already centered around origin
      const imgW = data.width;
      const imgH = data.height;
      Object.values(data.shapes).forEach(shape => {
        if (!shape.vertices || shape.vertices.length < 3) return;
        rawPolygons.push({ verts: shape.vertices, centered: true, imgW, imgH });
      });
    } else {
      // Old PhysicsEditor format
      (data.fixtures || []).forEach(fix => {
        if (!fix.vertices) return;
        if (Array.isArray(fix.vertices[0])) {
          fix.vertices.forEach(poly => { if (poly.length >= 3) rawPolygons.push({ verts: poly, centered: false }); });
        } else if (typeof fix.vertices[0] === 'object' && 'x' in fix.vertices[0]) {
          if (fix.vertices.length >= 3) rawPolygons.push({ verts: fix.vertices, centered: false });
        }
      });
    }

    if (rawPolygons.length === 0) return [];

    const texture = this.textures.get(name).getSourceImage();
    const cx = texture.width  / 2;
    const cy = texture.height / 2;

    const result = [];
    rawPolygons.forEach(({ verts, centered, imgW, imgH }) => {
      const refW = imgW || texture.width;
      const refH = imgH || texture.height;
      const sx = c.w / refW;
      const sy = c.h / refH;

      // Normalise to centered {x, y} objects
      const normalized = verts.map(v => ({
        x: Array.isArray(v) ? v[0] : (centered ? v.x : v.x - cx),
        y: Array.isArray(v) ? v[1] : (centered ? v.y : v.y - cy),
      }));

      const deduped = normalized.filter((v, i) => {
        if (i === 0) return true;
        const p = normalized[i - 1];
        return Math.abs(v.x - p.x) > 0.5 || Math.abs(v.y - p.y) > 0.5;
      });
      if (deduped.length < 3) return;

      const arr = new Float32Array(deduped.length * 2);
      deduped.forEach((v, i) => {
        arr[i * 2]     = toR(v.x * sx);
        arr[i * 2 + 1] = toR(v.y * sy);
      });

      let area = 0;
      const n = arr.length / 2;
      for (let i = 0; i < n; i++) {
        const x1 = arr[i*2], y1 = arr[i*2+1];
        const x2 = arr[((i+1)%n)*2], y2 = arr[((i+1)%n)*2+1];
        area += (x1 * y2 - x2 * y1);
      }
      if (Math.abs(area) / 2 < 0.002) return;
      result.push(arr);
    });

    return result;
  }

  /* ──────────────────── CREATE RAPIER FOOD BODY ────────────────── */
  createFoodBody(name, screenX, screenY, angleRad) {
    const RAPIER   = this.RAPIER;
    const c        = cfg(name);
    const polygons = this.scaledShapes[name] || [];

    const rigidBody = this.rapierWorld.createRigidBody(
      RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(toR(screenX), toR(screenY))
        .setRotation(angleRad)
        .setLinearDamping(0.6)
        .setAngularDamping(0.9)
    );

    let added = 0;
    polygons.forEach((verts, i) => {
      try {
        const cd = RAPIER.ColliderDesc.convexHull(verts);
        if (!cd) return;
        cd.setDensity(c.density)
          .setFriction(c.friction)
          .setFrictionCombineRule(RAPIER.CoefficientCombineRule.Average)
          .setRestitution(0.03)
          .setRestitutionCombineRule(RAPIER.CoefficientCombineRule.Min);
        this.rapierWorld.createCollider(cd, rigidBody);
        added++;
      } catch (e) {
        console.warn(`Part ${i} of "${name}" failed:`, e.message);
      }
    });

    if (added === 0) {
      this.rapierWorld.createCollider(
        RAPIER.ColliderDesc.cuboid(toR(c.w * 0.45), toR(c.h * 0.45))
          .setDensity(c.density).setFriction(c.friction).setRestitution(0.03),
        rigidBody
      );
    }
    return rigidBody;
  }

  /* ──────────────────── DEBUG RENDERER ─────────────────────────── */
  drawDebug() {
    if (!this.debugGraphics) return;
    this.debugGraphics.clear();
    this.foodBodies.forEach(({ rigidBody, name }) => {
      const bp  = rigidBody.translation();
      const rot = rigidBody.rotation();
      const cos = Math.cos(rot), sin = Math.sin(rot);
      (this.scaledShapes[name] || []).forEach(verts => {
        this.debugGraphics.lineStyle(1.5, 0x00ff00, 0.9);
        this.debugGraphics.beginPath();
        const n = verts.length / 2;
        for (let i = 0; i <= n; i++) {
          const idx = i % n;
          const lx = verts[idx*2], ly = verts[idx*2+1];
          const wx = bp.x + (lx*cos - ly*sin);
          const wy = bp.y + (lx*sin + ly*cos);
          if (i === 0) this.debugGraphics.moveTo(toPx(wx), toPx(wy));
          else         this.debugGraphics.lineTo(toPx(wx), toPx(wy));
        }
        this.debugGraphics.strokePath();
      });
    });
  }

  /* ──────────────────────── FOOD QUEUE ─────────────────────────── */
  refillQueue() {
    const deck = [...FOODS, ...FOODS, ...FOODS];
    Phaser.Utils.Array.Shuffle(deck);
    this.foodQueue = deck;
  }

  nextFoodName() {
    if (this.foodQueue.length === 0) this.refillQueue();
    return this.foodQueue.pop();
  }

  getNextFoodName() {
    if (this.foodQueue.length === 0) this.refillQueue();
    return this.foodQueue[this.foodQueue.length - 1]; // peek at next without popping
  }

  /* ──────────────────────── SPAWN PREVIEW ──────────────────────── */
  spawnNextFood() {
    this.previewAngle = 0;
    this.currentFoodName = this.nextFoodName();
    const c = cfg(this.currentFoodName);
    if (this.previewImage) this.previewImage.destroy();

    this.previewAngle = 0;

    this.previewImage = this.add.image(this.aimX, this.currentSpawnY, this.currentFoodName)
      .setDisplaySize(c.w, c.h)
      .setAlpha(0.75)
      .setRotation(0)
      .setDepth(100);

    this.canDrop = true;

    // Reset rotation counter for new food
    this.rotationsLeft = 12;
    this.nextFoodRevealed = false;
    this.events.emit('rotationCountUpdate', this.rotationsLeft);
    this.events.emit('nextFoodStatusUpdate', false);
  }

  /* ────────────────────── DROP FOOD ───────────────────────────── */
  dropCurrentFood() {
    if (!this.canDrop || this.gameOver) return;
    // In vs_bot mode, only the player can call this
    if (this.gameMode === 'vs_bot' && this.currentTurn !== 'player') return;
    this._executeDrop();
  }

  // Called by BotController — bypasses the player-turn guard
  botDrop() {
    if (!this.canDrop || this.gameOver) return;
    this._executeDrop();
  }

  _executeDrop() {
    this.canDrop = false;
    musicManager.soundDrop();

    // Snapshot the stable tower state BEFORE this drop (used by rescue).
    // In vs_bot mode, only snapshot on the player's turn.
    const isPlayerDrop = this.currentTurn === 'player';
    if (!this.rescueUsed && (this.gameMode === 'solo' || isPlayerDrop)) {
      this._takeSnapshot();
    }

    // Track who dropped this food (used by triggerGameOver)
    this.lastDropper = this.currentTurn;

    const name   = this.currentFoodName;
    const c      = cfg(name);
    const x      = this.aimX;
    const spawnY = this.currentSpawnY;
    const angle  = this.previewAngle;

    if (this.previewImage) { this.previewImage.destroy(); this.previewImage = null; }
    this.aimLine.clear();

    const rigidBody = this.createFoodBody(name, x, spawnY, angle);
    const image = this.add.image(x, spawnY, name)
      .setDisplaySize(c.w, c.h)
      .setRotation(angle)
      .setDepth(this.foodBodies.length + 10);

    this.foodBodies.push({ rigidBody, image, name });

    // Score is credited only after the food settles without causing collapse.
    const dropper = this.currentTurn;
    this.afterDropSettle(dropper);
  }

  afterDropSettle(dropper) {
    if (this.gameMode === 'vs_bot') {
      this._waitSettleThenNextTurn(dropper);
    } else {
      this._waitSettleAndScoreSolo();
      this.time.delayedCall(1800, () => {
        if (this.gameOver) return;
        this.currentSpawnY = this.getSpawnY();
        this.spawnNextFood();
      });
    }
  }

  // Solo: credit score once stack settles (tower hasn't collapsed).
  _waitSettleAndScoreSolo() {
    const check = this.time.addEvent({
      delay: 300,
      loop: true,
      callback: () => {
        if (this.gameOver) { check.remove(); return; }

        const allSettled = this.foodBodies.every(({ rigidBody }) => {
          const vel = rigidBody.linvel();
          const av  = rigidBody.angvel();
          return Math.abs(vel.x) < 0.5 && Math.abs(vel.y) < 0.5 && Math.abs(av) < 0.5;
        });

        if (allSettled && this.foodBodies.length > 0) {
          check.remove();
          this.score++;
          this.events.emit('scoreUpdate', this.score);
          this.calculateFinalHeight();
        }
      },
    });
  }

  _waitSettleThenNextTurn(dropper) {
    // Poll every 300ms; once everything has settled, credit score, flip turn, spawn.
    // Enforce a minimum 800ms wait so drops don't feel instant.
    const minWaitDone = { value: false };
    this.time.delayedCall(800, () => { minWaitDone.value = true; });

    const check = this.time.addEvent({
      delay: 300,
      loop: true,
      callback: () => {
        if (this.gameOver) { check.remove(); return; }

        const allSettled = this.foodBodies.every(({ rigidBody }) => {
          const vel = rigidBody.linvel();
          const av  = rigidBody.angvel();
          return Math.abs(vel.x) < 0.5 && Math.abs(vel.y) < 0.5 && Math.abs(av) < 0.5;
        });

        if (allSettled && minWaitDone.value && this.foodBodies.length > 0) {
          check.remove();

          // Credit score for the dropper now that the food has settled safely.
          this.score++;
          if (dropper === 'player') {
            this.playerScore++;
            this.events.emit('playerScoreUpdate', this.playerScore);
          } else {
            this.botScore++;
            this.events.emit('botScoreUpdate', this.botScore);
          }

          this.calculateFinalHeight();

          this.currentSpawnY = this.getSpawnY();
          this.currentTurn   = (this.currentTurn === 'player') ? 'bot' : 'player';
          this.events.emit('turnChanged', this.currentTurn);

          this.spawnNextFood();

          if (this.currentTurn === 'bot') {
            this.botController.takeTurn();
          }
        }
      },
    });
  }


  /* ───────────────────────── GAME OVER ─────────────────────────── */
  triggerGameOver() {
    if (this.gameOver) return;
    this.gameOver = true;
    incrementGameCount();
    musicManager.soundTowerFall();
    this.canDrop  = false;
    if (this.previewImage) { this.previewImage.destroy(); this.previewImage = null; }
    this.aimLine.clear();

    // Offer rescue if: not already used, snapshot exists, and the PLAYER caused the collapse.
    const offerRescue =
      !this.rescueUsed &&
      this.lastStableSnapshot !== null &&
      this.lastDropper === 'player';

    if (offerRescue) {
      this.events.emit('offerRescue');
    } else {
      this.events.emit('gameOver', {
        foods:       this.score,
        towerHeight: this.logicalHeight || 0,
        loser:       this.gameMode === 'vs_bot' ? this.lastDropper : null,
        playerScore: this.playerScore,
        botScore:    this.botScore,
      });
    }
  }

  // Called by UIScene when the player dismisses the rescue offer without watching
  declineRescue() {
    this.events.emit('gameOver', {
      foods:       this.score,
      towerHeight: this.logicalHeight || 0,
      loser:       this.gameMode === 'vs_bot' ? this.lastDropper : null,
      playerScore: this.playerScore,
      botScore:    this.botScore,
    });
  }

  // Restore the tower to the last stable state (called after ad watched)
  rescueGame() {
    if (!this.lastStableSnapshot) return;
    this.rescueUsed = true;

    const snap = this.lastStableSnapshot;
    this.lastStableSnapshot = null;

    // Clear current food bodies
    this.foodBodies.forEach(({ rigidBody, image }) => {
      try { this.rapierWorld.removeRigidBody(rigidBody); } catch(e) {}
      if (image && image.scene) image.destroy();
    });
    this.foodBodies = [];

    // Recreate physics world
    if (this.rapierWorld) { try { this.rapierWorld.free(); } catch(e) {} }
    this.rapierWorld = new this.RAPIER.World({ x: 0, y: 10 });

    // Recreate ground
    const GROUND_H = Math.max(20, 28 * S);
    const groundBody = this.rapierWorld.createRigidBody(
      this.RAPIER.RigidBodyDesc.fixed()
        .setTranslation(toR(this.platformX), toR(this.groundY + GROUND_H / 2))
    );
    this.rapierWorld.createCollider(
      this.RAPIER.ColliderDesc
        .cuboid(toR(this.platformW / 2), toR(GROUND_H / 2))
        .setFriction(0.9).setRestitution(0.0),
      groundBody
    );

    // Restore foods from snapshot (at rest — zero velocity)
    snap.foods.forEach(({ name, x, y, rot }) => {
      const c         = cfg(name);
      const rigidBody = this.createFoodBody(name, x, y, rot);
      rigidBody.setLinvel({ x: 0, y: 0 }, true);
      rigidBody.setAngvel(0, true);
      const image = this.add.image(x, y, name)
        .setDisplaySize(c.w, c.h)
        .setRotation(rot)
        .setDepth(this.foodBodies.length + 10);
      this.foodBodies.push({ rigidBody, image, name });
    });

    // Restore state
    this.score          = snap.score;
    this.logicalHeight  = snap.logicalHeight;
    this.maxTowerHeight = snap.maxTowerHeight;
    this.currentSpawnY  = snap.currentSpawnY;
    this.foodQueue      = [...snap.foodQueue];
    this.gameOver       = false;
    this.cameras.main.scrollY = snap.cameraScrollY;
    this.currentTurn    = 'player';
    this.lastDropper    = 'player';

    this.events.emit('rescueComplete', { score: this.score });
    this.events.emit('scoreUpdate', this.score);
    this.events.emit('heightUpdate', this.logicalHeight);
    if (this.gameMode === 'vs_bot') {
      this.events.emit('turnChanged', 'player');
    }

    this.time.delayedCall(300, () => {
      this.currentSpawnY = this.getSpawnY();
      this.spawnNextFood();
    });
  }

  _takeSnapshot() {
    this.lastStableSnapshot = {
      foods: this.foodBodies.map(({ rigidBody, name }) => {
        const pos = rigidBody.translation();
        return {
          name,
          x:   toPx(pos.x),
          y:   toPx(pos.y),
          rot: rigidBody.rotation(),
        };
      }),
      score:          this.score,
      logicalHeight:  this.logicalHeight,
      maxTowerHeight: this.maxTowerHeight,
      currentSpawnY:  this.currentSpawnY,
      foodQueue:      [...this.foodQueue],
      cameraScrollY:  this.cameras.main.scrollY,
      nextFoodRevealed: this.nextFoodRevealed,
    };
  }

  showPaused() {
    const W   = this.scale.width;
    const H   = this.scale.height;
    const dpr = window.devicePixelRatio || 1;
    const S   = Math.min((W / dpr) / 420, 1.8);
    const sd  = S * dpr;

    this.pauseContainer = this.add.container(0, 0).setDepth(20);

    const cardW = Math.min(320 * sd, W * 0.85);

    const gap       = 55 * sd;
    const titleY    = H / 2 - gap * 1.5;
    const subtitleY = titleY  + gap;
    const resumeY   = subtitleY + gap;
    const homeY     = resumeY  + gap;

    const cardH       = homeY - titleY + gap * 1.2;
    const cardCentreY = (titleY + homeY) / 2;

    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.7);
    const card    = this.add.rectangle(W / 2, cardCentreY, cardW, cardH, 0x0a1628, 0.97)
      .setStrokeStyle(3 * dpr, 0xffdd00);

    const title = this.add.text(W / 2, titleY, '⏸  PAUSED', {
      fontSize: `${26 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif', color: '#ffcc00',
    }).setOrigin(0.5);

    const subtitle = this.add.text(W / 2, subtitleY, 'Your score will be lost if you quit here', {
      fontSize: `${13 * sd}px`, fontFamily: 'Arial, sans-serif', color: '#aaaaaa',
      wordWrap: { width: cardW * 0.85 }, align: 'center',
    }).setOrigin(0.5);

    const resumeBtnW = Math.min(240 * sd, W * 0.6);
    const resumeBtnH = 60 * sd;
    const resumeBtn  = this.add.rectangle(W / 2, resumeY, resumeBtnW, resumeBtnH, 0x9944cc)
      .setInteractive({ useHandCursor: true });
    const resumeText = this.add.text(W / 2, resumeY, '▶  Resume', {
      fontSize: `${24 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif', color: '#1a1a2e',
    }).setOrigin(0.5);

    resumeBtn.on('pointerover',  () => resumeBtn.setFillStyle(0xbb66ee));
    resumeBtn.on('pointerout',   () => resumeBtn.setFillStyle(0x9944cc));
    resumeBtn.on('pointerdown',  () => {
      const gameScene = this.scene.get('GameScene');
      if (gameScene) gameScene.resumeGame();
    });

    const homeBtnW = Math.min(200 * sd, W * 0.5);
    const homeBtnH = 50 * sd;
    const homeBtn  = this.add.rectangle(W / 2, homeY, homeBtnW, homeBtnH, 0x4488ff)
      .setInteractive({ useHandCursor: true });
    const homeBtnText = this.add.text(W / 2, homeY, '🏠  Home', {
      fontSize: `${20 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffffff', stroke: '#000000', strokeThickness: 3 * dpr,
    }).setOrigin(0.5);

    homeBtn.on('pointerover',  () => homeBtn.setFillStyle(0x66aaff));
    homeBtn.on('pointerout',   () => homeBtn.setFillStyle(0x4488ff));
    homeBtn.on('pointerdown',  () => {
      this.scene.stop('GameScene');
      this.scene.stop('UIScene');
      this.scene.start('HomeScene');
    });

    this.pauseContainer.add([
      overlay, card, title, subtitle,
      resumeBtn, resumeText,
      homeBtn, homeBtnText,
    ]);
  }
// ── Pause game ─────────────────────────────────
  pauseGame() {
    if (this.gameOver) return;
    this.rapierWorld && this.scene.pause('GameScene');
    this.events.emit('gamePaused');
  }

  resumeGame() {
    this.scene.resume('GameScene');
    this.events.emit('gameResumed');
    this.ignoreDrop = true;
    this.time.delayedCall(500, () => { this.ignoreDrop = false; });
  }
}
